import {
  Alert,
  AlertIcon,
  Box,
  Button,
  FormControl,
  FormLabel,
  Heading,
  HStack,
  Input,
  InputGroup,
  InputLeftAddon,
  Spinner,
  Stack,
  Text,
} from "@chakra-ui/react";
import { useQuery } from "@tanstack/react-query";
import React from "react";
import { auth } from "../../../../core/auth";
import Page from "../../../../shared/components/Page";
import useApi from "../../../../shared/hooks/useApi";
import ReplacePaymentMethod from "./ReplacePaymentMethod";
import { AgencyBillingSummary } from "./types";

export default function AgencyBillingPage() {
  const authState = auth.getAuthOrFail();
  const isAdmin = authState.data.agencyMember.agencyAdmin;

  return (
    <Page>
      <Page.Header>
        <Page.Title>Billing</Page.Title>
      </Page.Header>

      <Page.Content>{isAdmin ? <AgencyBillingContent /> : <NotAdmin />}</Page.Content>
    </Page>
  );
}

function NotAdmin() {
  return (
    <Alert status="warning">
      <AlertIcon />
      Only agency admins can view billing details.
    </Alert>
  );
}

function AgencyBillingContent() {
  const { queries } = useApi();
  const summaryQuery = useQuery(queries.agencyBilling.summary());

  if (summaryQuery.isLoading) {
    return (
      <Box py={10} textAlign="center">
        <Spinner />
      </Box>
    );
  }

  if (summaryQuery.isError || summaryQuery.data === undefined) {
    return (
      <Alert status="error">
        <AlertIcon />
        Failed to load billing information.
      </Alert>
    );
  }

  return (
    <Stack p={6} spacing={10}>
      <PricingInformation />
      <CardDetails summary={summaryQuery.data} />
    </Stack>
  );
}

function PricingInformation() {
  const [setup, setSetup] = React.useState("");
  const [monthly, setMonthly] = React.useState("");
  const [discount, setDiscount] = React.useState("");
  const [months, setMonths] = React.useState("");
  const [locked, setLocked] = React.useState<string | null>(null);

  const lockPricing = () => {
    const monthlyNum = Number(monthly) || 0;
    const discountNum = Number(discount) || 0;
    const setupNum = Number(setup) || 0;
    const monthsNum = Number(months) || 0;
    let text = "Payment plan - ";
    if (monthsNum > 0) {
      text += `$${monthlyNum - discountNum}/month for the first `;
      text += monthsNum > 1 ? `${monthsNum} months` : "month";
      text += ", then ";
    }
    text += `$${monthlyNum}/month.`;
    if (setupNum > 0) {
      text += ` Setup fee: $${setupNum}.`;
    }
    setLocked(text);
  };

  return (
    <Stack maxW="2xl" spacing={4}>
      <Heading size="md">Pricing Information</Heading>
      {locked !== null ? (
        <Text color="green.600">{locked}</Text>
      ) : (
        <>
          <FormControl>
            <FormLabel>Setup Fee</FormLabel>
            <InputGroup>
              <InputLeftAddon>$</InputLeftAddon>
              <Input
                placeholder="Setup fee"
                type="number"
                value={setup}
                onChange={(e) => setSetup(e.target.value)}
              />
            </InputGroup>
          </FormControl>
          <FormControl>
            <FormLabel>Monthly Plan Price</FormLabel>
            <InputGroup>
              <InputLeftAddon>$</InputLeftAddon>
              <Input
                placeholder="Price"
                type="number"
                value={monthly}
                onChange={(e) => setMonthly(e.target.value)}
              />
            </InputGroup>
          </FormControl>
          <FormControl>
            <FormLabel>Introduction Discount</FormLabel>
            <InputGroup>
              <InputLeftAddon>$</InputLeftAddon>
              <Input
                placeholder="Monthly discount"
                type="number"
                value={discount}
                onChange={(e) => setDiscount(e.target.value)}
              />
            </InputGroup>
          </FormControl>
          <FormControl>
            <FormLabel>Discount Duration</FormLabel>
            <Input
              placeholder="# of months"
              type="number"
              value={months}
              onChange={(e) => setMonths(e.target.value)}
            />
          </FormControl>
          <Box>
            <Button colorScheme="blue" onClick={lockPricing}>
              Lock Pricing
            </Button>
          </Box>
        </>
      )}
    </Stack>
  );
}

function CardDetails(props: { summary: AgencyBillingSummary }) {
  const pm = props.summary.defaultPaymentMethod;
  const cardNumber = pm?.last4 != null ? `•••• •••• •••• ${pm.last4}` : "—";
  const expiry =
    pm?.expMonth != null && pm?.expYear != null
      ? `${String(pm.expMonth).padStart(2, "0")} / ${String(pm.expYear).slice(-2)}`
      : "—";

  return (
    <Stack maxW="2xl" spacing={4}>
      <Heading size="md">Card Details</Heading>
      <Stack spacing={3}>
        <Row label="Name on card" value={props.summary.customerName ?? "—"} />
        <Row label="Card number" value={cardNumber} />
        <Row label="Expiry" value={expiry} />
        <Row label="Billing address" value={props.summary.customerAddress ?? "—"} />
      </Stack>
      <Box>
        <ReplacePaymentMethod hasExisting={pm !== null} />
      </Box>
    </Stack>
  );
}

function Row(props: { label: string; value: string }) {
  return (
    <HStack align="start" spacing={6}>
      <Text color="gray.600" w={40}>
        {props.label}
      </Text>
      <Text>{props.value}</Text>
    </HStack>
  );
}
